import React, { Fragment, useState } from "react";

// Apollo Client
import { gql, useMutation, useQuery } from "@apollo/client";

// External Components
import { Spinner } from "react-bootstrap";

// External Utilities
import { uploadClient } from "../../..";

// Custom Components and Hooks
import RequestMutationPopup from "../Popups/RequestMutationPopup/RequestMutationPopup";
import useRequestMutationForm, {
  RESET_TO_INITIAL_STATE,
} from "../Popups/RequestMutationPopup/hooks/useRequestMutationForm";
import Header from "./Header/Header";
import RequestTable from "./Table/RequestTable";
import WarningModal from "../../WarningModal/WarningModal";
import { notify } from "../../Notify/Notify";
import useRequestTable from "./Table/hooks/useRequestTable";
import useFilter, {
  initialFilterState as initialFilterStateConstant,
} from "./Table/hooks/useAdvanceFilter";

// External Libraries
import deepEqual from "fast-deep-equal";
import { sort } from "fast-sort";
import {
  endOfDay,
  isAfter,
  isBefore,
  isSameDay,
  isWithinInterval,
  parseISO,
  startOfDay,
} from "date-fns";
import { subDays } from "date-fns";
import { useSelector } from "react-redux";

// Graphql Related
export const GET_ALL_REQUESTS = gql`
  query GetAllRequests {
    data: documentrequests {
      id
      title
      recurringInterval
      sentOn: createdAt
      dueDate
      documentsCount
      userCount: mailingListUsersCount
      submittedBy: doneStatusUsersCount
      status
      lastUpdated: modifiedAt
      createdBy {
        id
      }
    }
  }
`;

const CANCEL_REQUEST = gql`
  mutation CancelRequest($ids: [String]!) {
    cancelDocumentRequest(ids: $ids) {
      success
    }
  }
`;

const RightSection = () => {
  // Queries
  const { loading: isTableDataLoading, data } = useQuery(GET_ALL_REQUESTS, {
    client: uploadClient,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  // Local states
  const [isRequestMutationPopupOpen, setIsRequestMutationPopupOpen] =
    useState(false);
  const [isCancelRequestPopupOpen, setIsCancelRequestPopupOpen] =
    useState(false);
  const { state, onChange } = useRequestMutationForm();

  // Advance Filter Related
  const { state: initialFilterState, handleChange: handleFilterChange } =
    useFilter();
  const { state: finalFilterState, handleChange: handleFinalFilterChange } =
    useFilter();

  const [anchorElForAdvanceFilter, setAnchorElForAdvanceFilter] =
    React.useState(null);

  // Table Related
  const currentUserInfo = useSelector((state) => state.currentUserInfo);
  const isAdvanceFilterActive = !deepEqual(
    finalFilterState,
    initialFilterStateConstant
  );
  const {
    table,
    pageIndex,
    globalFilter,
    setGlobalFilter,
    getAllSelectedRowIds,
    resetAllRowSelection,
    setPageIndex,
    getAllSelectedRows,
  } = useRequestTable(
    filterTableData(
      feedExpiredStatus(data?.data ?? []),
      finalFilterState,
      currentUserInfo?.id ?? "no-id",
      isAdvanceFilterActive
    )
  );
  // Handlers

  // Create request handler
  const handleOnCloseForRequestMutationPopup = () => {
    setIsRequestMutationPopupOpen(false);
    onChange(RESET_TO_INITIAL_STATE);
  };

  // Advance Filter Related
  const handleCloseForAdvanceFilter = (isSave = false) => {
    if (isSave) {
      handleFinalFilterChange("setEntireState", initialFilterState);
      setPageIndex(0);
    } else {
      handleFilterChange("setEntireState", finalFilterState);
    }
    setAnchorElForAdvanceFilter(null);
  };
  const openAdvanceFiler = (event) => {
    setAnchorElForAdvanceFilter(event?.currentTarget);
  };

  // Mutations
  const [cancelRequest, { loading: isCancelRequestLoading }] = useMutation(
    CANCEL_REQUEST,
    {
      client: uploadClient,
      refetchQueries: [{ query: GET_ALL_REQUESTS }],
      onError: (error) => {
        notify("Error", "Something went wrong.", "error");
        console.error("Cancel Request Mutation Error:", error);
      },
      onCompleted: () => {
        notify("Success", "Request(s) are successfully canceled", "success");
        resetAllRowSelection();
        setIsCancelRequestPopupOpen(false);
      },
      awaitRefetchQueries: true,
    }
  );

  // Local variables
  const selectedRows = getAllSelectedRowIds();
  return (
    <Fragment>
      {false && (
        <div className="loader-icon">
          <Spinner animation="border" role="status" />
        </div>
      )}
      {true && (
        <div>
          <Header
            setIsRequestMutationPopupOpen={setIsRequestMutationPopupOpen}
            openCancelRequestPopup={() => setIsCancelRequestPopupOpen(true)}
            selectedRows={selectedRows}
          />
          <RequestTable
            table={table}
            isLoading={isTableDataLoading}
            pageIndex={pageIndex}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            isAdvanceFilterOpen={Boolean(anchorElForAdvanceFilter)}
            openAdvanceFiler={openAdvanceFiler}
            handleCloseForAdvanceFilter={handleCloseForAdvanceFilter}
            handleFilterChange={handleFilterChange}
            anchorElForAdvanceFilter={anchorElForAdvanceFilter}
            advanceFilterState={initialFilterState}
            isAdvanceFilterActive={isAdvanceFilterActive}
            setPageIndex={setPageIndex}
          />
        </div>
      )}
      <RequestMutationPopup
        state={state}
        onChange={onChange}
        isEdit={false}
        isOpen={isRequestMutationPopupOpen}
        onClose={handleOnCloseForRequestMutationPopup}
      />
      <WarningModal
        headingTitle="Cancelling Request"
        descriptionText="You are about to cancel an open request with pending documents. Doing so will mark the request as cancelled and prevent requestees from adding any further documents."
        isOpen={isCancelRequestPopupOpen}
        onCloseHandler={(isProceed) => {
          if (isProceed) {
            const allSelectedRows = getAllSelectedRows();
            const cancelledRowSelected = allSelectedRows?.filter(
              (u) => u?.status === "CANCELLED"
            );
            if (cancelledRowSelected.length > 0) {
              const errorMsg = generateErrorMessageForCancelledRequest(
                allSelectedRows,
                cancelledRowSelected
              );
              notify("Error", errorMsg, "error");
              setIsCancelRequestPopupOpen(false);
              return;
            }
            cancelRequest({
              variables: {
                ids: selectedRows,
              },
            });
          } else {
            setIsCancelRequestPopupOpen(false);
          }
        }}
        isProceedBtnDisable={isCancelRequestLoading}
      />
    </Fragment>
  );
};

RightSection.propTypes = {};

export default RightSection;

// Helper functions

/**
 * Filter the given data array based on the provided filters.
 * @param {Object[]} data - The array of objects to filter.
 * @param {Object} filters - The filters to apply.
 * @param {Object} filters.sentOnFilter - The filter for sentOn date.
 * @param {string} filters.sentOnFilter.option - The filter option for sentOn date. Possible values: "On", "Before", "After", "Between", "Is blank".
 * @param {string} filters.sentOnFilter.date - The date to filter sentOn date.
 * @param {string} filters.sentOnFilter.secondDate - The second date to filter sentOn date (used if option is "Between").
 * @param {Object} filters.dueDateFilter - The filter for dueDate.
 * @param {string} filters.dueDateFilter.option - The filter option for dueDate. Possible values: "On", "Before", "After", "Between", "Is blank".
 * @param {string} filters.dueDateFilter.date - The date to filter dueDate.
 * @param {string} filters.dueDateFilter.secondDate - The second date to filter dueDate (used if option is "Between").
 * @param {Object} filters.statusFilters - The filters for status.
 * @returns {Object[]} The filtered array of objects.
 */
function filterTableData(
  data,
  filters,
  currentUserId = "no-id",
  isAdvanceFilterActive = true
) {
  let filteredMasterData = data.filter((item) => {
    // Filter by sentOn date
    if (filters.sentOnFilter.option !== "Is blank") {
      if (filters.sentOnFilter.option === "On" && filters.sentOnFilter.date) {
        const itemSentOn = startOfDay(parseISO(item.sentOn));
        const filterDate = startOfDay(parseISO(filters.sentOnFilter.date));
        if (!isSameDay(itemSentOn, filterDate)) {
          return false;
        }
      }
      if (
        filters.sentOnFilter.option === "Before" &&
        filters.sentOnFilter.date
      ) {
        const itemSentOn = startOfDay(parseISO(item.sentOn));
        const filterDate = startOfDay(parseISO(filters.sentOnFilter.date));
        if (
          isAfter(itemSentOn, filterDate) ||
          isSameDay(itemSentOn, filterDate)
        ) {
          return false;
        }
      }
      if (
        filters.sentOnFilter.option === "After" &&
        filters.sentOnFilter.date
      ) {
        const itemSentOn = startOfDay(parseISO(item.sentOn));
        const filterDate = startOfDay(parseISO(filters.sentOnFilter.date));
        if (
          isBefore(itemSentOn, filterDate) ||
          isSameDay(itemSentOn, filterDate)
        ) {
          return false;
        }
      }
      if (
        filters.sentOnFilter.option === "Between" &&
        filters.sentOnFilter.date &&
        filters.sentOnFilter.secondDate
      ) {
        const itemSentOn = startOfDay(parseISO(item.sentOn));
        const filterDate = startOfDay(parseISO(filters.sentOnFilter.date));
        const secondDate = endOfDay(parseISO(filters.sentOnFilter.secondDate));
        if (
          !isWithinInterval(itemSentOn, { start: filterDate, end: secondDate })
        ) {
          return false;
        }
      }
    } else if (filters.sentOnFilter.option === "Is blank" && item.sentOn) {
      return false;
    }

    // Filter by dueDate
    if (filters.dueDateFilter.option !== "Is blank") {
      if (filters.dueDateFilter.option === "On" && filters.dueDateFilter.date) {
        const itemDueDate = startOfDay(parseISO(item.dueDate));
        const filterDate = startOfDay(parseISO(filters.dueDateFilter.date));
        if (!isSameDay(itemDueDate, filterDate)) {
          return false;
        }
      }
      if (
        filters.dueDateFilter.option === "Before" &&
        filters.dueDateFilter.date
      ) {
        const itemDueDate = startOfDay(parseISO(item.dueDate));
        const filterDate = startOfDay(parseISO(filters.dueDateFilter.date));
        if (
          isAfter(itemDueDate, filterDate) ||
          isSameDay(itemDueDate, filterDate)
        ) {
          return false;
        }
      }
      if (
        filters.dueDateFilter.option === "After" &&
        filters.dueDateFilter.date
      ) {
        const itemDueDate = startOfDay(parseISO(item.dueDate));
        const filterDate = startOfDay(parseISO(filters.dueDateFilter.date));
        if (
          isBefore(itemDueDate, filterDate) ||
          isSameDay(itemDueDate, filterDate)
        ) {
          return false;
        }
      }
      if (
        filters.dueDateFilter.option === "Between" &&
        filters.dueDateFilter.date &&
        filters.dueDateFilter.secondDate
      ) {
        const itemDueDate = startOfDay(parseISO(item.dueDate));
        const filterDate = startOfDay(parseISO(filters.dueDateFilter.date));
        const secondDate = endOfDay(parseISO(filters.dueDateFilter.secondDate));
        if (
          !isWithinInterval(itemDueDate, { start: filterDate, end: secondDate })
        ) {
          return false;
        }
      }
    } else if (filters.dueDateFilter.option === "Is blank" && item.dueDate) {
      return false;
    }

    // Filter by status
    // Check if any status filter is selected
    const anyStatusFilterSelected = Object.values(filters.statusFilters).some(
      (filter) => filter
    );

    // Check if any request owner filter is selected
    const anyRequestFilterSelected =
      filters?.requestsFilter?.onlyMyRequests ||
      filters?.requestsFilter?.othersRequests;

    // Check if item matches any selected status filter (OR relation)
    const allStatusFiltersSelected = anyStatusFilterSelected
      ? Object.keys(filters.statusFilters).some(
          (filter) =>
            filters.statusFilters[filter] &&
            item.status.toUpperCase() === filter.toUpperCase()
        )
      : true;

    // Check if item matches any selected request owner filter (OR relation)
    const ownRequestsFilterState =
      filters?.requestsFilter?.onlyMyRequests ?? false;
    const othersRequestsFilterState =
      filters?.requestsFilter?.othersRequests ?? false;
    const createdByCurrentUser = item?.createdBy?.id === currentUserId;

    const isAnyStatusFilterSelected = Object.values(
      filters.statusFilters ?? {}
    ).some((status) => status);

    // Status filter
    if (
      !ownRequestsFilterState &&
      !ownRequestsFilterState &&
      isAnyStatusFilterSelected
    ) {
      const isAllStatusSelected = Object.values(
        filters.statusFilters ?? { dummy: false }
      ).every((status) => status);
      if (isAllStatusSelected) return true;

      return Object.keys(filters.statusFilters).some(
        (filter) =>
          filters.statusFilters[filter] &&
          item.status.toUpperCase() === filter.toUpperCase()
      );
    }

    const allRequestFiltersSelected =
      ownRequestsFilterState || othersRequestsFilterState;
    const allRequestFiltersMatched = ownRequestsFilterState
      ? createdByCurrentUser
      : true || othersRequestsFilterState
      ? !createdByCurrentUser
      : true;

    // Return true if no filters are selected
    const noFiltersSelected =
      !anyStatusFilterSelected && !anyRequestFilterSelected;

    // Return true only if item matches all selected root-level filters (statusFilters AND requestsFilter)
    return (
      (allStatusFiltersSelected &&
        allRequestFiltersSelected &&
        allRequestFiltersMatched) ||
      noFiltersSelected
    );
  });
  // Handle recurringRequest condition
  // TODO: Update the cond. based on flag
  // if( filters?.requestsFilter?.recurringRequest === true ){
  //   filteredMasterData = filteredMasterData.filter(u => u.isOriginal)
  // }

  const ownRequests = filteredMasterData.filter(
    (item) => item?.createdBy?.id === currentUserId
  );
  const otherUsersRequests = filteredMasterData.filter(
    (item) => item?.createdBy?.id !== currentUserId
  );
  const ownRequestsWhichIsNotOlderThanSevenDays = sort(
    ownRequests.filter((item) => {
      const lastWeekDateFromToday = startOfDay(subDays(new Date(), 8));
      const itemDueDate = startOfDay(parseISO(item.dueDate));
      return isAfter(itemDueDate, lastWeekDateFromToday);
    })
  ).asc((u) => new Date(u.dueDate));

  const otherUsersRequestsWhichIsNotOlderThanSevenDays = sort(
    otherUsersRequests.filter((item) => {
      const lastWeekDateFromToday = startOfDay(subDays(new Date(), 8));
      const itemDueDate = startOfDay(parseISO(item.dueDate));
      return isAfter(itemDueDate, lastWeekDateFromToday);
    })
  ).asc((u) => new Date(u.dueDate));

  const ownRequestWhichIsOlderThanSevenDays = sort(
    ownRequests.filter((item) => {
      const lastWeekDateFromToday = startOfDay(subDays(new Date(), 8));
      const itemDueDate = startOfDay(parseISO(item.dueDate));
      return !isAfter(itemDueDate, lastWeekDateFromToday);
    })
  ).asc((u) => new Date(u.lastUpdated));

  const otherUsersRequestsWhichIsOlderThanSevenDays = sort(
    otherUsersRequests.filter((item) => {
      const lastWeekDateFromToday = startOfDay(subDays(new Date(), 8));
      const itemDueDate = startOfDay(parseISO(item.dueDate));
      return !isAfter(itemDueDate, lastWeekDateFromToday);
    })
  ).asc((u) => new Date(u.lastUpdated));

  return [
    ...ownRequestsWhichIsNotOlderThanSevenDays,
    ...otherUsersRequestsWhichIsNotOlderThanSevenDays,
    ...ownRequestWhichIsOlderThanSevenDays,
    ...otherUsersRequestsWhichIsOlderThanSevenDays,
  ];
}

export function generateErrorMessageForCancelledRequest(
  allSelectedRows,
  cancelledRowSelected
) {
  const totalSelectedRows = allSelectedRows.length;
  const totalCancelledRows = cancelledRowSelected.length;
  if (totalCancelledRows === 1) {
    return "The request has already been cancelled.";
  } else if (totalSelectedRows === totalCancelledRows) {
    return "The requests have already been cancelled.";
  } else {
    return "Some requests have already been cancelled.";
  }
}

function feedExpiredStatus(data) {
  if (!Array.isArray(data)) {
    return [];
  }
  const TODAY = startOfDay(parseISO(new Date().toISOString()));

  return data.map((req) => {
    const dueDate = endOfDay(parseISO(req.dueDate));
    if (req.status?.toLowerCase() === "pending" && isAfter(TODAY, dueDate)) {
      return {
        ...req,
        status: "expired",
      };
    }
    return req;
  });
}
